// ============================================================
// TwoPersonChecksPanel.tsx — Admin › Security: "Two-person checks", the firm's
// one switch for whether a second person must approve (lib/twoPerson on the
// server). A read-first card with a pencil, as the AI briefing card. Live:
// another admin's change arrives on the firm's 'settings' topic. Audited on the
// server (who, when, on → off); admin-only there too.
// ============================================================
import React, { useCallback, useEffect, useState } from 'react'
import { ErrorNote } from '../shared/ActionBox'
import { UnsavedGuard } from '../../hooks/useUnsavedChangesGuard'
import { SettingsCard, SettingsSaved, SettingsSwitch, SettingsToggleRow, S_NOTE } from './settingsUi'
import { EditFooter, EditPencil } from '../settlement/settlementUi'
import { errorText } from '../billing/billingUi'
import { getTwoPersonChecksApi, setTwoPersonChecksApi } from '../../api/casevoApi'
import { useFirmChange } from '../../utils/liveEvents'

export const TWO_PERSON_NOTE = 'Most firms keep this on. Turning it off lets one person approve their own bills and statements; Casevo still records who did what.'

const TwoPersonChecksPanel: React.FC = () => {
  const [value, setValue] = useState<boolean | null>(null)
  const [draft, setDraft] = useState<boolean | null>(null)
  const [loadErr, setLoadErr] = useState<string | null>(null)
  const [saveErr, setSaveErr] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const load = useCallback(() => {
    getTwoPersonChecksApi().then(setValue).catch((e: unknown) => setLoadErr(errorText(e, 'Couldn’t load this setting.')))
  }, [])
  useEffect(load, [load])
  useFirmChange(['settings'], load)

  // Keyboard focus never drops to the page: into the switch, back to the pencil.
  const toSwitch = (): void => { window.setTimeout(() => document.querySelector<HTMLElement>('#two-person-checks [role="switch"]')?.focus(), 0) }
  const toPencil = (): void => { window.setTimeout(() => document.querySelector<HTMLElement>('#two-person-checks [data-edit-pencil]')?.focus(), 0) }
  const save = async (): Promise<void> => {
    if (draft === null) return
    setSaving(true); setSaveErr(null)
    try {
      setValue(await setTwoPersonChecksApi(draft))
      setDraft(null); setSaved(true); toPencil()
    } catch (e) {
      setSaveErr(errorText(e, 'Couldn’t save this setting.'))
    } finally { setSaving(false) }
  }
  if (loadErr) return <ErrorNote>{loadErr}</ErrorNote>
  const editing = draft !== null
  return (
    <>
      <UnsavedGuard dirty={editing && draft !== value && !saving} />
      <SettingsCard id="two-person-checks" icon="shield" title="Two-person checks" sub="Whether a second person must approve bills, statements and recent contact changes."
        editing={editing}
        right={editing || value === null ? null : <>{saved && <SettingsSaved />}<EditPencil title="Two-person checks" onClick={() => { setSaved(false); setSaveErr(null); setDraft(value); toSwitch() }} /></>}
        footer={editing ? <EditFooter blocked={false} saving={saving} error={saveErr} onCancel={() => { setDraft(null); setSaveErr(null); toPencil() }} onSave={() => { void save() }} /> : undefined}>
        {editing ? (
          <SettingsToggleRow title="Two-person checks" desc={TWO_PERSON_NOTE}
            control={<SettingsSwitch checked={!!draft} onChange={() => setDraft(!draft)} label="Two-person checks" disabled={saving} />} />
        ) : (
          <dl className="m-0" data-read-view="two-person-checks">
            <dt className="text-[13px] text-casevo-text-soft">Status</dt>
            <dd className="m-0 mt-[2px] text-[14px] font-medium text-casevo-forest">{value === null ? 'Loading…' : value ? 'On' : 'Off'}</dd>
            <p className={`${S_NOTE} mt-[10px] mb-0 leading-[1.5]`}>{TWO_PERSON_NOTE}</p>
          </dl>
        )}
      </SettingsCard>
    </>
  )
}

export default TwoPersonChecksPanel
